import './image.scss';

import Swiper from 'swiper';
import { Pagination, Navigation, A11y } from 'swiper/modules';

/**
 * Image slider wrapper around Swiper.
 *
 * Responsibilities:
 * - Initialize a Swiper slider when there is more than one slide
 * - Wire up pagination progressbar and prev/next buttons
 * - Add a tiny visual effect on pagination during slide transitions
 *
 * Usage:
 * const slider = new Image($root).initialize();
 */
class Image {
    /**
     * @param {HTMLElement} $element Root element marked with `[data-image-slider="root"]`.
     */
    constructor ($element) {
        /** @type {HTMLElement} */
        this.$root = $element;

        /** @type {NodeListOf<HTMLElement>} */
        this.$$slides = this.$root?.querySelectorAll('[data-image-slider="slide"]') ?? [];
        /** @type {HTMLElement|null} */
        this.$prev = this.$root?.querySelector('.slider-controls__prev');
        /** @type {HTMLElement|null} */
        this.$next = this.$root?.querySelector('.slider-controls__next');
        /** @type {HTMLElement|null} */
        this.$pagination = this.$root?.querySelector('.slider-controls__pagination');

        /** @type {import('swiper').Swiper|null} */
        this.slider = null;
    }

    /**
     * Initialize the slider (idempotent). Only builds when there is more than one slide.
     * @returns {Image} this instance (for chaining)
     */
    initialize () {
        if (!this.$root) return this;
        if (this.slider) return this; // already initialized
        if (this.$$slides.length > 1) {
            this.#buildSlider();
        }
        return this;
    }

    /**
     * Backwards-compatible initializer name. Prefer initialize().
     * @deprecated Use initialize() or the private #buildSlider() via initialize.
     * @returns {void}
     */
    initSlider () { this.#buildSlider(); }

    /**
     * Destroy the Swiper instance and clean up references.
     * @returns {void}
     */
    destroy () {
        this.slider?.destroy?.(true, true);
        this.slider = null;
    }

    // ========== private helpers ==========

    /**
     * Build the Swiper instance with configured modules and hooks.
     * @returns {void}
     */
    #buildSlider () {
        if (!this.$root) return;
        this.slider = new Swiper(this.$root, {
            modules: [Pagination, Navigation, A11y],
            slidesPerView: 'auto',
            spaceBetween: 0,
            speed: 1000,
            slideClass: 'image__images-item',
            wrapperClass: 'image__images-inner',
            pagination: {
                el: this.$pagination,
                type: 'progressbar'
            },
            navigation: {
                nextEl: this.$next,
                prevEl: this.$prev,
                disabledClass: 'slider-controls__disabled'
            },
            on: {
                slideChangeTransitionStart: () => { this.$pagination?.classList.add('is--active'); },
                slideChangeTransitionEnd: () => { this.$pagination?.classList.remove('is--active'); }
            }
        });
    }
}

export { Image };

// Auto-initializer for newly loaded content blocks
window.addEventListener('content.loaded', (e) => {
    const { $context } = e?.detail ?? {}; // optional chaining -> love it
    if (!$context) return;

    $context.querySelectorAll('[data-image-slider="root"]').forEach(($el) => {
        $el.API = ($el.API ?? new Image($el)).initialize();
    });
});
